import { Activity, ChevronDown, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import type { VstDebugRecord } from '../global';
import { history, subscribe } from '../vestigo';

/**
 * Console de debug fixo: espelha cada evento realmente enviado ao collector
 * (via o interceptor de sendBeacon/fetch em index.html). Mostra nome, params e
 * consent — a verificação visual antes de olhar o painel do Vestigo.
 */
export function DebugConsole() {
  const [events, setEvents] = useState<VstDebugRecord[]>(() => [...history()]);
  const [open, setOpen] = useState(true);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return subscribe((rec) => setEvents((prev) => [...prev, rec]));
  }, []);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [events]);

  return (
    <div className="fixed bottom-0 right-0 z-50 w-full max-w-md font-mono">
      <div className="m-3 border border-dark/20 bg-deep text-[#e7ebe6] shadow-2xl">
        <button
          onClick={() => setOpen((o) => !o)}
          className="flex w-full items-center justify-between gap-2 border-b border-white/10 px-4 py-2.5 text-left"
        >
          <span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.25em] text-white/70">
            <Activity size={13} className="text-accent" />
            Eventos Vestigo
            <span className="bg-accent px-1.5 py-0.5 text-[9px] text-white">{events.length}</span>
          </span>
          <ChevronDown
            size={14}
            className={`text-white/50 transition-transform ${open ? '' : 'rotate-180'}`}
          />
        </button>

        {open && (
          <>
            <div ref={listRef} className="max-h-72 overflow-y-auto">
              {events.length === 0 ? (
                <p className="px-4 py-6 text-center text-[11px] text-white/40">
                  Aguardando eventos… o PageView deve aparecer ao carregar.
                </p>
              ) : (
                events.map((rec, i) => <Row key={i} rec={rec} />)
              )}
            </div>
            <div className="flex items-center justify-between border-t border-white/10 px-4 py-2">
              <span className="text-[9px] uppercase tracking-widest text-white/30">
                → /t/e
              </span>
              <button
                onClick={() => setEvents([])}
                className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-white/40 hover:text-white/80"
              >
                <Trash2 size={11} /> Limpar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ rec }: { rec: VstDebugRecord }) {
  const b = rec.body;
  const time = new Date(rec.at).toLocaleTimeString('pt-BR');
  const params = b.p && Object.keys(b.p).length ? b.p : null;
  const hasUser = b.d && Object.keys(b.d).length > 0;
  return (
    <div className="border-b border-white/5 px-4 py-2 text-[11px] leading-relaxed">
      <div className="flex items-center gap-2">
        <span className="text-accent">{time}</span>
        <span className="font-semibold text-white">{b.n ?? '—'}</span>
        {typeof b.x === 'boolean' && (
          <span className={b.x ? 'text-ok' : 'text-warn'}>consent:{b.x ? 'on' : 'off'}</span>
        )}
        {hasUser && <span className="text-white/50">+identify</span>}
        {b.g && <span className="text-white/50">trigger</span>}
      </div>
      {params && (
        <pre className="mt-1 whitespace-pre-wrap break-words text-[10px] text-white/50">
          {JSON.stringify(params)}
        </pre>
      )}
    </div>
  );
}
